import React, { useState } from 'react';
import Button from 'src/components/Button';
import Input from 'src/components/Input';
import * as client from 'src/services/client';
import { toast } from 'react-toastify';
import Select from 'src/components/Select';
import './service-log.scss';

const HOURS_OPTIONS = [...Array(24)].map((_, i) => ({ value: i, label: i }));
const MINUTES_OPTIONS = [...Array(60)].map((_, i) => ({ value: i, label: i }));

const getLocalLog = (serviceRequestId: string) => {
  try {
    const stored = JSON.parse(localStorage.getItem('ServiceLog') ?? 'null');
    return stored?.ServiceRequestId === serviceRequestId ? stored : null;
  } catch {
    return null;
  }
};

const parseTimePart = (time: string, index: number, splitter: string): number => {
  try {
    const parts = time.split('d');
    if (index === 0) return Number(parts[0]) || 0;
    const rest = parts[1];
    if (splitter === 'h') return Number(rest.split('h')[0].trim()) || 0;
    return Number(rest.split('h')[1].split('m')[0].trim()) || 0;
  } catch {
    return 0;
  }
};

const resolveTime = (time: string | undefined, serviceRequestId: string, key: 'ActualTime' | 'EstimatedTime'): string => {
  const local = getLocalLog(serviceRequestId);
  return local?.[key] ?? time ?? '';
};

const ServiceLog = ({ serviceRequestId, actualTime, estimatedTime }: {
  serviceRequestId: string;
  actualTime?: string;
  estimatedTime?: string;
}) => {
  const resolvedActual = resolveTime(actualTime, serviceRequestId, 'ActualTime');
  const resolvedEstimated = resolveTime(estimatedTime, serviceRequestId, 'EstimatedTime');

  const [estimateDay, setEstimateDay] = useState(() => parseTimePart(resolvedEstimated, 0, 'd'));
  const [estimateHour, setEstimateHour] = useState(() => parseTimePart(resolvedEstimated, 1, 'h'));
  const [estimateMinutes, setEstimateMinutes] = useState(() => parseTimePart(resolvedEstimated, 2, 'm'));

  const [actualDay, setActualDay] = useState(() => parseTimePart(resolvedActual, 0, 'd'));
  const [actualHour, setActualHour] = useState(() => parseTimePart(resolvedActual, 1, 'h'));
  const [actualMinutes, setActualMinutes] = useState(() => parseTimePart(resolvedActual, 2, 'm'));

  const saveServiceLog = async () => {
    try {
      const totalEstimateMinutes =
        Number(estimateDay) * 24 * 60 + Number(estimateHour) * 60 + Number(estimateMinutes);
      const totalActualMinutes =
        Number(actualDay) * 24 * 60 + Number(actualHour) * 60 + Number(actualMinutes);

      const params: any = {
        id: serviceRequestId,
        EstimatedTime: String(totalEstimateMinutes),
        ActualTime: String(totalActualMinutes),
      };

      const response: any = await client.saveServiceLog(params);

      if (response.ok || response.status === 200) {
        const json = await response.json();

        if (json.statusCode === 200) {
          const estStr = `${estimateDay || 0}d ${estimateHour || 0}h ${estimateMinutes || 0}m`;
          const actStr = `${actualDay || 0}d ${actualHour || 0}h ${actualMinutes || 0}m`;

          localStorage.setItem('ServiceLog', JSON.stringify({
            ServiceRequestId: serviceRequestId,
            ActualTime: actStr,
            EstimatedTime: estStr,
          }));

          toast.success('Service Log saved successfully.', { autoClose: 5000, position: 'top-center' });
        } else {
          toast.error('Sorry, we cannot save the Service Log at this time. Please try again later.', {
            autoClose: 5000,
            position: 'top-center',
          });
        }
      }
    } catch (ex) {
      console.error('Error saving service log:', ex);
    }
  };

  return (
    <div className="service-log">
      <div className="service-log-row">
        <div className="service-log-label-container">
          <span className="service-log-label">Estimated Time:</span>
        </div>
        <Input
          value={String(estimateDay)}
          onChange={(e: any) => setEstimateDay(e.target.value.replace(/\D/, ''))}
          block
          label="Days"
        />
        <Select
          value={estimateHour}
          options={HOURS_OPTIONS}
          label="Hours"
          onChange={(e: any) => setEstimateHour(e.target.value)}
          placeholder="Select Hours"
          required={false}
        />
        <Select
          value={estimateMinutes}
          options={MINUTES_OPTIONS}
          label="Minutes"
          onChange={(e: any) => setEstimateMinutes(e.target.value)}
          placeholder="Select Minutes"
          required={false}
        />
      </div>
      <div className="service-log-row">
        <div className="service-log-label-container">
          <span className="service-log-label">Actual Time:</span>
        </div>
        <Input
          value={String(actualDay)}
          onChange={(e: any) => setActualDay(e.target.value.replace(/\D/, ''))}
          block
          label="Days"
        />
        <Select
          value={actualHour}
          options={HOURS_OPTIONS}
          label="Hours"
          onChange={(e: any) => setActualHour(e.target.value)}
          placeholder="Select Hours"
          required={false}
        />
        <Select
          value={actualMinutes}
          options={MINUTES_OPTIONS}
          label="Minutes"
          onChange={(e: any) => setActualMinutes(e.target.value)}
          placeholder="Select Minutes"
          required={false}
        />
      </div>

      <div className="service-log-actions">
        <Button color="teal" size="small" onClick={saveServiceLog} label="Save" />
      </div>
    </div>
  );
};

export default ServiceLog;
